<template>
  <section class="px-5">
    <div class="text-fg-muted">
      <ul>
        <li
          class="grid grid-cols-2 font-normal text-sm items-center border-b py-3.5 px-4 lg:py-6"
        >
          <span>Amount</span>
          <span class="capitalize font-semibold">{{
            parseMoneyAll(
              beneficiaryDetails.amount.amount,
              beneficiaryDetails.amount.currency
            )
          }}</span>
        </li>
        <li
          class="grid grid-cols-2 font-normal text-sm items-center border-b py-3.5 px-4 lg:py-6"
        >
          <span>Beneficiary ID</span>
          <span
            >{{ maskID(beneficiaryDetails.id) }}
            <span
              class="cursor-pointer mb-1 ml-2"
              v-clipboard:copy="beneficiaryDetails.id"
              v-clipboard:success="onCopy"
              v-clipboard:error="onError"
            >
              <copy-icon />
            </span>
          </span>
        </li>
        <li
          class="grid grid-cols-2 font-normal text-sm items-center border-b py-3.5 px-4 lg:py-6"
        >
          <span>Status</span>
          <div>
            <span
              class="text-xs md:text-sm capitalize py-1 px-4 rounded-xl shadow-sm"
              :class="statusClass(beneficiaryDetails.status)"
            >
              {{ formatServerName(beneficiaryDetails.status) }}
            </span>
          </div>
        </li>
        <li
          class="grid grid-cols-2 font-normal text-sm items-center border-b py-3.5 px-4 lg:py-6"
        >
          <span>Time & Date</span>
          <span>{{ parseTime(beneficiaryDetails) || '--' }}</span>
        </li>
        <li
          class="grid grid-cols-2 font-normal text-sm items-center border-b py-3.5 px-4 lg:py-6"
        >
          <span>Account Name</span>
          <span class="capitalize">{{ beneficiaryDetails.name }}</span>
        </li>
        <li
          v-if="hasBankCode"
          class="grid grid-cols-2 font-normal text-sm items-center border-b py-3.5 px-4 lg:py-6"
        >
          <span>Bank Code</span>
          <span class="capitalize">{{
            beneficiaryDetails.details.bankCode || '--'
          }}</span>
        </li>
        <li
          v-if="hasBankName"
          class="grid grid-cols-2 font-normal text-sm items-center border-b py-3.5 px-4 lg:py-6"
        >
          <span>Bank Name</span>
          <span class="capitalize">{{
            beneficiaryDetails.details.bankName || '--'
          }}</span>
        </li>
        <li
          v-if="hasRoutingNumber"
          class="grid grid-cols-2 font-normal text-sm items-center border-b py-3.5 px-4 lg:py-6"
        >
          <span>Routing Number</span>
          <span class="capitalize">{{
            beneficiaryDetails.details.routingNumber || '--'
          }}</span>
        </li>
        <li
          v-if="hasSwiftCode"
          class="grid grid-cols-2 font-normal text-sm items-center border-b py-3.5 px-4 lg:py-6"
        >
          <span>Swift Code</span>
          <span class="capitalize">{{
            beneficiaryDetails.details.swiftCode || '--'
          }}</span>
        </li>
        <li
          class="grid grid-cols-2 font-normal text-sm items-center border-b py-3.5 px-4 lg:py-6"
        >
          <span>Account Number</span>
          <span class="capitalize">
            {{ accountNumber }}
          </span>
        </li>
        <li
          class="grid grid-cols-2 font-normal text-sm items-center border-b py-3.5 px-4 lg:py-6"
        >
          <span>Remarks</span>
          <span class="capitalize">{{ beneficiaryDetails.remarks }}</span>
        </li>
        <li
          class="grid grid-cols-2 font-normal text-sm items-center border-b py-3.5 px-4 lg:py-6"
        >
          <span>Lookup Info</span>
          <span class="capitalize">{{
            beneficiaryDetails.lookupInfo || '-'
          }}</span>
        </li>
        <li
          class="grid grid-cols-2 font-normal text-sm items-center border-b py-3.5 px-4 lg:py-6"
        >
          <span>Notes</span>
          <span class="capitalize">{{ beneficiaryDetails.notes || '-' }}</span>
        </li>

        <!-- intermediary bank details -->
        <template v-if="hasIntermediaryBankDetails">
          <p class="font-normal text-sm my-5 bg-gray-100 p-2">
            Intermediary Bank Details
          </p>
          <li
            v-for="(details, index) in intermediaryBankDetails"
            :key="index"
            class="grid grid-cols-2 font-normal text-sm items-center border-b py-3.5 px-4 lg:py-6"
          >
            <span>{{ details.label }}</span>
            <span class="capitalize">{{ details.value || '-' }}</span>
          </li>
        </template>
      </ul>
      <el-button
        v-if="isStatusNeedsUpdate"
        @click="updateHandler"
        class="bg-primary text-white rounded-lg shadow-sm px-8 text-center py-5 mb-5 w-full"
      >
        Update
      </el-button>
      <div v-if="isCompletedTransaction" class="gap-x-2 mt-6">
        <button
          class="w-full justify-center flex gap-x-2 bg-green-6 text-green-1 border border-green-1 py-4 rounded-lg px-4"
          @click="generateReceipt"
        >
          <download-icon />
          {{ isLoading ? 'Downloading...' : 'Download Receipt' }}
        </button>
      </div>
    </div>
    <vuehtml2pdf
      :manual-pagination="true"
      :filename="fileName"
      :pdf-quality="2"
      pdf-format="a4"
      pdf-content-width="800px"
      :enable-download="true"
      ref="DownloadComp"
      :show-layout="false"
    >
      <section slot="pdf-content">
        <generate-receipt-details :transferType="'Payout'" />
      </section>
    </vuehtml2pdf>
  </section>
</template>
<script>
import {
  GetCurrencyCountry,
  MaskWalletAddressTwo,
  AddComma,
  FormatServerName,
  ParseMoneyAll,
  StatusClass
} from '@/utils/helpers.js'
import getReceiptDetails from '@/mixins/global.js'
import userKinds from '@/mixins/userKinds.js'
import app from '@/mixins/app.js'

export default {
  name: 'CustomerDetailsDrawer',
  mixins: [getReceiptDetails, userKinds, app],
  data () {
    return {
      isLoading: false,
      payload: {
        id: ''
      }
    }
  },
  props: {
    beneficiaryDetails: {
      type: Object,
      default: () => {},
      required: true
    }
  },
  components: {
    CopyIcon: () => import('@/assets/icons/CopyIcon.vue'),
    DownloadIcon: () => import('@/assets/v2/icons/CloudDownloadIcon.vue'),
    GenerateReceiptDetails: () =>
      import('@/components/common/GenerateReceiptDetails.vue')
  },
  computed: {
    hasIntermediaryBankDetails () {
      return this.beneficiaryDetails.details.intermediaryBank?.swiftCode
    },
    intermediaryBankDetails () {
      return [
        {
          label: 'Swift Code',
          value: this.beneficiaryDetails.details.intermediaryBank.swiftCode
        },
        {
          label: 'Bank Name',
          value: this.beneficiaryDetails.details.intermediaryBank.bankName
        },
        {
          label: 'Address',
          value: this.beneficiaryDetails.details.intermediaryBank.bankAddress
        }
      ]
    },
    currentPaymentLinkTransactions () {
      return this.$store.state.paymentLink.paymentLinkTransactions.items
    },
    currentPaymentLinkDetails () {
      return this.$store.state.paymentLink.currentPaymentLinkDetails
    },
    isStatusNeedsUpdate () {
      return this.beneficiaryDetails.status === this.$STATUS.NEEDS_UPDATE
    },
    hasBankCode () {
      // The use of optional chaining on the template causes an error for some reason
      return this.beneficiaryDetails.details?.bankCode?.length > 0
    },
    hasBankName () {
      return this.beneficiaryDetails.details?.bankName?.length > 0
    },
    hasRoutingNumber () {
      return this.beneficiaryDetails.details?.routingNumber?.length > 0
    },
    hasSwiftCode () {
      return this.beneficiaryDetails.details?.swiftCode?.length > 0
    },
    accountNumber () {
      if (this.beneficiaryDetails.details.bankDetails) {
        return this.beneficiaryDetails.details.bankDetails.accountNumber
      } else if (this.beneficiaryDetails.details.accountNumber) {
        return this.beneficiaryDetails.details.accountNumber
      } else {
        return '--'
      }
    },
    isCompletedTransaction () {
      return this.beneficiaryDetails.status === this.$STATUS.COMPLETED
    },
    fileName () {
      return `${this.businessName || this.adminBusinessName} - (${
        this.beneficiaryDetails.amount.amount
      } ${this.beneficiaryDetails.amount.currency})`
    }
  },
  methods: {
    updateHandler () {
      this.$emit('toggle-view', 'edit-bulk-payout')
    },
    parseTime (row) {
      if (row.createdAt?.length < 1) {
        return '--'
      }

      const timeToPick = !this.isCompleted ? row.createdAt : row.updatedAt

      return this.tooltipDateContent(timeToPick)
    },
    statusClass (status) {
      return StatusClass(status)
    },
    parseMoneyAll (amount, currency) {
      return ParseMoneyAll(amount, currency)
    },
    addComma (val) {
      return AddComma(val.toString())
    },
    formatServerName (val) {
      return FormatServerName(val)
    },
    maskID (val) {
      return MaskWalletAddressTwo(val)
    },
    getCurrencyCountry (val) {
      return GetCurrencyCountry(val)
    },
    onCopy: function (e) {
      this.$notify.success({
        title: 'Copied'
      })
    },
    onError: function (e) {
      this.$notify.error('Error copying to clipboard!')
    },
    generateReceipt () {
      this.payload.id = this.beneficiaryDetails.id
      this.getReceiptDetails(this.payload)
    }
  }
}
</script>
<style scoped>
>>> .el-input__inner {
  background: var(--input-background);
  color: var(--input-text);
  padding-left: 2.8rem;
}
>>> .el-input__placeholder {
  color: var(--input-placeholder);
}
</style>
